import React from 'react'
import {
  blockIdentity,
  deleteContact,
  unblockIdentity,
  updateContact,
  type ContactDetailResponse,
} from '../api/contacts'
import useContactsDetailStore from '../store/contactsDetail'
import useContactsListStore from '../store/contactsList'

export interface UseContactDetailControllerOptions {
  stationId: number
  contactId: number | null
  isAdmin: boolean
  onMerged?: (intoId: number) => void
  onDeleted?: () => void
}

export interface ContactDetailController {
  detail: ContactDetailResponse | null
  loading: boolean
  error: string | null
  saving: boolean
  blocking: boolean
  deleting: boolean
  refresh: () => Promise<ContactDetailResponse | null>
  save: (values: { name: string | null; location: string | null; notes: string | null }) => Promise<void>
  toggleContactBlock: (blocked: boolean) => Promise<void>
  toggleIdentityBlock: (identityId: number, blocked: boolean) => Promise<void>
  deleteContact: () => Promise<{ ok: boolean; message?: string; reason?: 'not-admin' | 'has-identities' | 'error' }>
  deleteDisabledReason?: string
}

const useContactDetailController = ({
  stationId,
  contactId,
  isAdmin,
  onDeleted,
}: UseContactDetailControllerOptions): ContactDetailController => {
  const fetchDetail = useContactsDetailStore((state) => state.fetch)
  const setContactDetail = useContactsDetailStore((state) => state.setContact)
  const contactEntry = useContactsDetailStore(
    React.useCallback(
      (state) => (contactId ? state.byId[contactId] ?? null : null),
      [contactId],
    ),
  )

  const removeContactFromList = useContactsListStore((state) => state.removeContact)
  const updateContactBlockedState = useContactsListStore((state) => state.setBlockedState)

  const [saving, setSaving] = React.useState(false)
  const [blocking, setBlocking] = React.useState(false)
  const [deleting, setDeleting] = React.useState(false)

  const detail = contactEntry?.data ?? null
  const loading = contactEntry?.loading ?? false
  const error = contactEntry?.error ?? null

  const refresh = React.useCallback(async () => {
    if (!stationId || !contactId) {
      return null
    }
    return fetchDetail(stationId, contactId)
  }, [stationId, contactId, fetchDetail])

  const save = React.useCallback(
    async (values: { name: string | null; location: string | null; notes: string | null }) => {
      if (!stationId || !contactId) {
        return
      }
      setSaving(true)
      try {
        const response = await updateContact(contactId, {
          station_id: stationId,
          ...values,
        })
        setContactDetail(contactId, response, stationId)
        updateContactBlockedState(contactId, response.contact.blocked)
      } finally {
        setSaving(false)
      }
    },
    [stationId, contactId, setContactDetail, updateContactBlockedState],
  )

  const toggleContactBlock = React.useCallback(
    async (blocked: boolean) => {
      if (!stationId || !contactId) {
        return
      }
      setBlocking(true)
      try {
        const response = await updateContact(contactId, {
          station_id: stationId,
          block_contact: blocked,
        })
        setContactDetail(contactId, response, stationId)
        updateContactBlockedState(contactId, response.contact.blocked)
      } finally {
        setBlocking(false)
      }
    },
    [stationId, contactId, setContactDetail, updateContactBlockedState],
  )

  const toggleIdentityBlock = React.useCallback(
    async (identityId: number, blocked: boolean) => {
      if (!stationId || !contactId) {
        return
      }
      if (blocked) {
        await blockIdentity(identityId, stationId)
      } else {
        await unblockIdentity(identityId, stationId)
      }
      const latest = await fetchDetail(stationId, contactId)
      if (latest) {
        setContactDetail(contactId, latest, stationId)
      }
    },
    [stationId, contactId, fetchDetail, setContactDetail],
  )

  const deleteContactAction = React.useCallback(async () => {
    if (!stationId || !contactId) {
      return { ok: false, reason: 'error', message: 'Missing contact information.' }
    }

    if (!isAdmin) {
      return { ok: false, reason: 'not-admin', message: 'Only admins can delete contacts.' }
    }

    if (detail && detail.identities.length > 0) {
      return {
        ok: false,
        reason: 'has-identities',
        message: 'Remove linked identities before deleting this contact.',
      }
    }

    setDeleting(true)
    try {
      await deleteContact(contactId, stationId)
      removeContactFromList(contactId)
      onDeleted?.()
      return { ok: true }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to delete contact.'
      return { ok: false, reason: 'error', message }
    } finally {
      setDeleting(false)
    }
  }, [stationId, contactId, detail, isAdmin, removeContactFromList, onDeleted])

  const deleteDisabledReason = React.useMemo(() => {
    if (!isAdmin) {
      return 'Admin only'
    }
    if (detail && detail.identities.length > 0) {
      return 'Remove linked identities before deleting.'
    }
    return undefined
  }, [detail, isAdmin])

  return {
    detail,
    loading,
    error,
    saving,
    blocking,
    deleting,
    refresh,
    save,
    toggleContactBlock,
    toggleIdentityBlock,
    deleteContact: deleteContactAction,
    deleteDisabledReason,
  }
}

export default useContactDetailController
